import type { ProgramOnOffSessionApiGateway } from "@/modules/sessions/gateway/programOnOffSessionApiGateway"
import {
  OnOffSessionDetailsDtoSchema,
  type OnOffSessionDetailsDto,
  type OnOffSessionInputDto,
} from "@/modules/sessions/gateway/programOnOffSessionDto"
import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { defaultDateFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"

export class ProgramOnOffSessionApi implements ProgramOnOffSessionApiGateway {
  private baseUrl = "/sessions"
  private sessionBaseUrl = "sessions/on-off"
  private buildProgramOnOffSessionsUrl = (programId: string) => `programs/on-off/${programId}/sessions`
  private buildProgramOnOffSessionsByDateUrl = (programId: string) => `programs/on-off/${programId}/sessionsByDate`
  private buildProgramOnOffSessionsByIndexUrl = (programId: string) => `programs/on-off/${programId}/sessionsByIndex`

  constructor(private api: ApiClient) {}

  async getDetailedSession(id: string, programId: string, sessionDate?: string): Promise<OnOffSessionDetailsDto> {
    const url = sessionDate
      ? `${this.buildProgramOnOffSessionsByDateUrl(programId)}/${id}`
      : `${this.buildProgramOnOffSessionsByIndexUrl(programId)}/${id}`

    const resultDto = await this.api.get<OnOffSessionDetailsDto>(url, {
      params: {
        date: sessionDate,
      },
    })

    return LoggerService.parseApiDto(OnOffSessionDetailsDtoSchema, resultDto.data)
  }

  async getSessions(startDate: string, endDate: string, programId: string): Promise<OnOffSessionDetailsDto[]> {
    const result = await this.api.get<OnOffSessionDetailsDto[]>(this.buildProgramOnOffSessionsByDateUrl(programId), {
      params: {
        minDate: dayjs(startDate).format(defaultDateFormat),
        maxDate: dayjs(endDate).format(defaultDateFormat),
      },
    })

    return result.data.map((sessionDto) => LoggerService.parseApiDto(OnOffSessionDetailsDtoSchema, sessionDto))
  }

  async getSessionsByIndex(startIndex: number, endIndex: number, programId: string): Promise<OnOffSessionDetailsDto[]> {
    const result = await this.api.get<OnOffSessionDetailsDto[]>(
      `${this.buildProgramOnOffSessionsByIndexUrl(programId)}`,
      {
        params: {
          indexMin: startIndex,
          indexMax: endIndex,
        },
      },
    )

    return result.data.map((sessionDto) => LoggerService.parseApiDto(OnOffSessionDetailsDtoSchema, sessionDto))
  }

  async createSession(programId: string, sessionDraft: OnOffSessionInputDto) {
    const resultDto = await this.api.post<OnOffSessionDetailsDto>(
      this.buildProgramOnOffSessionsUrl(programId),
      sessionDraft,
    )
    return LoggerService.parseApiDto(OnOffSessionDetailsDtoSchema, resultDto.data)
  }

  async updateSession(id: string, programId: string, sessionDraft: OnOffSessionInputDto) {
    const resultDto = await this.api.put<OnOffSessionDetailsDto>(
      `${this.buildProgramOnOffSessionsUrl(programId)}/${id}`,
      sessionDraft,
    )
    return LoggerService.parseApiDto(OnOffSessionDetailsDtoSchema, resultDto.data)
  }

  async copyWeekFromAnOnOffProgramToAnother(
    originProgramId: string,
    destinationProgramId: string,
    originStartIndex: number,
    originEndIndex: number,
    destinationStartIndex: number,
    overwrite: boolean = false,
  ) {
    await this.api.post(`${this.sessionBaseUrl}/duplicate-week`, {
      originProgramId,
      destinationProgramId,
      originStartIndex,
      originEndIndex,
      destinationStartIndex,
      overwrite,
    })
  }

  async setSessionCompleted(sessionId: string, completed: boolean): Promise<void> {
    await this.api.post(`${this.baseUrl}/${sessionId}/set-completed`, { completed })
  }
}
